import { createRouter, createWebHistory } from 'vue-router'
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { routes } from '@/routes/routerMap'
import { checkRoles, isEmptyObject, logOut } from '@/utils'
import { useAuthStore } from '@/stores/authStore.ts'
import { getAuthInfo, getUserInfo, logout } from '@/apis/userInfoService.ts'
import { RouterName } from '@/routes/config.ts'
import { useGlobalStore } from '@/stores/globalStore.ts'

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach(
  async (
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    // Redirect mobile
    if (window.innerWidth < 768 && to.name !== RouterName.LOGIN && to.meta.hasMobilePage) {
      window.location.replace(`${import.meta.env.VITE_WP_WDN_MOBILE_URL}${to.fullPath}`)
      return
    }

    if (to.meta.title) {
      document.title = typeof to.meta.title === 'string' ? to.meta.title : 'Quản lý mạng lưới'
    }

    const authStore = useAuthStore()
    const globalStore = useGlobalStore()

    // Fetch auth info nếu chưa có
    if (!authStore.auth.isAuthentication) {
      globalStore.setLoading(true)
      try {
        const res = await getAuthInfo()
        if (res?.accessToken) {
          authStore.setAccessToken(res.accessToken)
          authStore.setRefreshToken(res.refreshToken)
          authStore.setMqttToken(res.mqttToken)
          authStore.setMemberCompanyCode(res.memberCompanyCode)
          authStore.setMemberCompanyId(res.memberCompanyId)
          const rs = await getUserInfo()
          authStore.setUserInfo(rs.body)
          authStore.setShowModalChangePassword(rs?.body?.rqrChangePwd || false)
          authStore.setIsAuthentication(true)
        }
      } catch (e) {
        console.log(e)
      } finally {
        globalStore.setLoading(false)
      }
    }

    const isLoginPage = to.name === RouterName.LOGIN
    const isAuthenticated = authStore.auth.isAuthentication

    // Trang login: đã đăng nhập thì redirect về welcome
    if (isLoginPage) {
      return isAuthenticated ? next({ name: RouterName.WELCOME }) : next()
    }

    // Trang không cần auth
    if (to.name === 'error') {
      return next()
    }

    // Chưa đăng nhập -> logout và dừng navigation
    if (!isAuthenticated) {
      await logout()
      logOut()
      return
    }

    // Kiểm tra quyền
    const { userInfo } = authStore.auth

    if (userInfo?.email && !userInfo?.authorization?.length) {
      return next('/403')
    }

    if (to?.meta?.uriRole && !isEmptyObject(userInfo)) {
      return checkRoles(to.meta.uriRole as string) ? next() : next('/403')
    }

    return next()
  }
)

export default router
